<template>
  <div class="blackbody-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$λ_peak = b / T$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan"><label>黑体温度 T</label><span class="val">{{ temp }} <small>K</small></span></div>
          <div class="hud-item orange"><label>峰值波长 λ_max</label><span class="val">{{ (2897772 / temp).toFixed(0) }} <small>nm</small></span></div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 450 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <linearGradient id="rainbow-zone" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#a855f7" stop-opacity="0.3"/><stop offset="50%" stop-color="#22c55e" stop-opacity="0.3"/><stop offset="100%" stop-color="#ef4444" stop-opacity="0.3"/>
             </linearGradient>
             <filter id="glow-peak"><feGaussianBlur stdDeviation="4" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 坐标轴 -->
          <g stroke="#334155" stroke-width="1.5">
             <line x1="50" y1="220" x2="420" y2="220" /> <!-- X: 0 to 4000nm -->
             <line x1="50" y1="220" x2="50" y2="30" />  <!-- Y: Intensity -->
          </g>
          <text x="410" y="235" fill="#475569" font-size="9" font-weight="900">λ / nm</text>

          <!-- 可见光彩虹带 (380-750nm) -->
          <rect :x="50 + (380/4000)*360" y="30" :width="(370/4000)*360" height="190" fill="url(#rainbow-zone)" />
          <text :x="50 + (560/4000)*360" y="235" fill="#64748b" font-size="8" text-anchor="middle">可见光区</text>

          <!-- 普朗克曲线 (Planck's Curve) -->
          <path :d="curvePath" fill="none" :stroke="starColor" stroke-width="3" filter="url(#glow-peak)" />
          <path :d="curvePath + ' L 410,220 V 220 H 50 Z'" :fill="starColor" opacity="0.15" />

          <!-- 峰值点标注 -->
          <circle v-if="peakPos" :cx="peakPos.x" :cy="peakPos.y" r="5" :fill="starColor" filter="url(#glow-peak)" />

          <!-- 模拟恒星颜色 -->
          <g transform="translate(360, 80)">
             <circle r="40" :fill="starColor" filter="url(#glow-peak)" />
             <text y="55" text-anchor="middle" fill="#94a3b8" font-size="9">星体色彩模拟</text>
             <text y="-45" text-anchor="middle" :fill="starColor" font-size="11" font-weight="900">恒星光谱: {{ starType }}</text>
          </g>
        </svg>

        <div class="physics-caption">
           规律：温度每升高一倍，辐射强度随 T⁴ 级数暴增，且峰值 λ 显著左移
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">黑体绝对温度 (Temperature)：{{ temp }} K</label>
                 <el-slider v-model="temp" :min="1000" :max="15000" :step="100" size="small"></el-slider>
              </div>
              <div class="preset-group">
                 <el-button size="mini" plain @click="temp = 3000">红巨星</el-button>
                 <el-button size="mini" type="warning" plain @click="temp = 5800">太阳</el-button>
                 <el-button size="mini" type="primary" plain @click="temp = 12000">蓝巨星</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-warning-outline"></i>
           <p><strong>实战物理：</strong>经典的瑞利-金斯公式预言了“紫外灾难”，即高频辐射趋于无限。普朗克建立的这道完美曲线，通过<strong>能量子假设</strong>拯救了物理学。<strong>观察：</strong>将滑块拉向 10000K 以上，你会发现星体颜色从黄变白再变冷浅蓝，这正是宇宙中高能恒星的真实写照。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'BlackbodySpectrumViz',
  data() {
    return {
      temp: 5800
    }
  },
  computed: {
    starColor() {
       const t = this.temp;
       if (t < 3500) return '#f87171'; // Red
       if (t < 5000) return '#fb923c'; // Orange
       if (t < 6500) return '#fbbf24'; // Yellow (Sun)
       if (t < 9000) return '#f8fafc'; // White
       if (t < 12000) return '#7dd3fc'; // Blue White
       return '#3b82f6'; // Deep Blue
    },
    starType() {
       const t = this.temp;
       if (t < 3500) return 'M型 (红星)';
       if (t < 5000) return 'K型 (橙红星)';
       if (t < 6000) return 'G型 (黄星/太阳)';
       if (t < 7500) return 'F型 (黄白星)';
       if (t < 10000) return 'A型 (白星)';
       return 'B/O型 (蓝巨星)';
    },
    curvePath() {
       let points = [];
       // Planck Law constants (Simulated Scale)
       const c1 = 1e16; 
       const c2 = 1.44e7; // hc/k in nm*K

       // Dynamic scale factor to keep peak in visible window
       // Intensity grows as T^4, so we must normalize visually
       const normY = 4e-12 * Math.pow(this.temp, 3.5);

       for (let lambda = 50; lambda <= 4000; lambda += 20) {
          const x = 50 + (lambda / 4000) * 360;
          const exponent = c2 / (lambda * this.temp);
          const intensity = (c1 / Math.pow(lambda, 5)) * (1 / (Math.exp(exponent) - 1));
          
          let y = 220 - (intensity / normY) * 160;
          y = Math.max(30, Math.min(220, y));
          points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
       }
       return "M " + points.join(" L ");
    },
    peakPos() {
       const lambdaMax = 2897772 / this.temp;
       const x = 50 + (lambdaMax / 4000) * 360;
       // Simply find top of the curve for Y
       const c1 = 1e16; const c2 = 1.44e7;
       const normY = 4e-12 * Math.pow(this.temp, 3.5);
       const intensity = (c1 / Math.pow(lambdaMax, 5)) * (1 / (Math.exp(c2/(lambdaMax*this.temp)) - 1));
       const y = 220 - (intensity / normY) * 160;
       return { x, y: Math.max(30, y) };
    }
  }
}
</script>

<style scoped lang="scss">
.blackbody-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #dcfce7; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.preset-group { display: flex; gap: 4px; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .flex-1 { flex: 1; } .items-center { align-items: center; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #dcfce7; display: flex; align-items: center; gap: 10px; i { color: #16a34a; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 11px; color: #166534; line-height: 1.5; strong { color: #14532d; } } }
</style>
